import { generateUUID } from "../../utils/uuidUtils";
import { useParams } from 'react-router-dom';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useWidgetWebSocket } from '../../hooks/useWidgetWebSocket';
import { createChatbotService, ChatbotConfiguration as ChatbotConfigType } from '../../services/chatbotService';
import { Button, Input, Layout, Typography, Modal, Dropdown, Card, Upload, message, Flex, Progress, Divider, Space } from 'antd';
import { DownOutlined, EllipsisOutlined, LeftOutlined, ArrowUpOutlined, PaperClipOutlined, CloseOutlined, AudioOutlined, FileTextOutlined, UploadOutlined, MessageOutlined, ArrowRightOutlined, ReloadOutlined, DownloadOutlined, SmileOutlined, MehOutlined, FrownOutlined, CheckCircleOutlined, CloseCircleFilled } from '@ant-design/icons';
import arrowUpIcon from '../../assets/icons/arrow-up.svg';
import axios from 'axios';
import chatbotAnimation from '../../assets/img/chatbotAnimation.mp4';
import { Client } from '@stomp/stompjs';
import useVoiceAgent from '../../features/voiceagent/hooks/useVoiceAgent.ts';
import Editor from '@monaco-editor/react';
import { ProgressSteps } from '../Progress';
import { RealTimeAssistantPanel, RealTimeAssistantData } from '../../features/realtime-assistant/components';
import './Chatbot.css'

const { Header, Content, Footer } = Layout;
const { Text } = Typography;

export interface ChatbotProps {
    wsUrl: string;
    chatbotId?: string;
    channelId?: string;
    voiceWsUrl?: string;
    urlApi: string;
    rabbitmqWebSocketUrl?: string;
    isWeb?: boolean;
    isPreview?: boolean;
    primaryColor?: string;
    headerColor?: string;
    iconColor?: string;
    botName?: string;
    onArtifactAccept?: (artifact: { content: string, language?: string }) => void;
    autoAcceptArtifacts?: boolean; // If true, artifacts are auto-accepted. If false, shows Accept button
}

interface Message {
    text: string;
    isUser: boolean;
    timestamp?: number;
    id?: string;
}

// ChatbotConfiguration type is now imported from chatbotService
type ChatbotConfiguration = ChatbotConfigType;


// Función para detectar y extraer artifacts
const detectArtifacts = (text: string): { hasArtifact: boolean, content: string, language?: string, cleanText: string } => {
    const artifactPattern = /@artifactinit(.*?)@artifactend/gs;
    const match = artifactPattern.exec(text);

    if (match) {
        const content = match[1]
            .replace(/```[a-z]*\n?/gi, '')
            .replace(/```/g, '')
            .trim();
        const cleanText = text.replace(artifactPattern, '').trim();

        // Detectar el lenguaje basado en el contenido con mayor precisión
        let language = 'plaintext';

        // JavaScript/TypeScript - buscar palabras clave y sintaxis específica
        if (content.match(/\b(function|const|let|var|=>|import|export|class|interface|type)\b/) ||
            content.match(/console\.(log|error|warn)/) ||
            content.match(/\$\{.*\}/) || // template literals
            content.match(/\.js$|\.ts$|\.jsx$|\.tsx$/m)) {
            language = 'javascript';
        }
        // TypeScript específico
        else if (content.match(/\b(interface|type|enum|namespace|declare)\b/) ||
            content.match(/:.*\s*=/) || // type annotations
            content.match(/\w+\s*:\s*(string|number|boolean|object)/)) {
            language = 'typescript';
        }
        // HTML - buscar tags y estructura HTML
        else if (content.match(/<\/?[a-z][\s\S]*>/i) ||
            content.match(/<!DOCTYPE|<html|<head|<body|<div|<span|<p|<h[1-6]|<img|<a\s/i)) {
            language = 'html';
        }
        // CSS - buscar selectores y propiedades CSS
        else if (content.match(/\w+\s*\{[^}]*\}/) ||
            content.match(/[.#][\w-]+\s*\{/) ||
            content.match(/\b(color|background|margin|padding|font-size|display|position)\s*:/) ||
            content.match(/@(media|import|keyframes)/)) {
            language = 'css';
        }
        // JSON - estructura de objeto/array válida
        else if ((content.trim().startsWith('{') && content.trim().endsWith('}')) ||
            (content.trim().startsWith('[') && content.trim().endsWith(']'))) {
            try {
                JSON.parse(content);
                language = 'json';
            } catch (e) {
                // Si no es JSON válido, mantener como texto
            }
        }
        // Python - palabras clave y sintaxis específica
        else if (content.match(/\b(def|class|import|from|if __name__|print|range|len)\b/) ||
            content.match(/^\s*#.*$/m) || // comentarios con #
            content.match(/:\s*$/m)) { // líneas que terminan con :
            language = 'python';
        }
        // SQL - palabras clave de base de datos
        else if (content.match(/\b(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|FROM|WHERE|JOIN|GROUP BY|ORDER BY)\b/i)) {
            language = 'sql';
        }
        // XML - similar a HTML pero más estricto
        else if (content.match(/<\?xml/) ||
            (content.includes('<') && content.includes('>') && content.match(/<\/\w+>/))) {
            language = 'xml';
        }
        // Markdown - encabezados, links, código
        else if (content.match(/^#+\s/m) ||
            content.match(/\[.*\]\(.*\)/) ||
            content.match(/```/) ||
            content.match(/^\*\s|^-\s|^\d+\.\s/m)) {
            language = 'markdown';
        }
        // YAML - estructura con indentación y :
        else if (content.match(/^\s*\w+:\s*(.*)?$/m) &&
            !content.includes('{') && !content.includes('[')) {
            language = 'yaml';
        }
        // Bash/Shell - comandos y sintaxis de shell
        else if (content.match(/^\s*#!/) ||
            content.match(/\b(echo|cd|ls|mkdir|rm|cp|mv|grep|find|sudo)\b/) ||
            content.match(/\$\w+|\${.*}/)) {
            language = 'bash';
        }

        return {
            hasArtifact: true,
            content,
            language,
            cleanText
        };
    }

    return {
        hasArtifact: false,
        content: '',
        cleanText: text
    };
};

// Utility function to detect if a message is a file upload message
const isFileUploadMessage = (text: string): boolean => {
    return text.startsWith('File uploaded:') && text.includes('S3 Key:') && text.includes('Download URL:');
};

// Function to parse file information from message text
const parseFileInfo = (text: string) => {
    const lines = text.split('\n');
    const filename = lines[0]?.replace('File uploaded: ', '') || '';
    const size = lines[1]?.replace('Size: ', '') || '';
    const s3Key = lines[2]?.replace('S3 Key: ', '') || '';
    const downloadUrl = lines[3]?.replace('Download URL: ', '') || '';

    return { filename, size, s3Key, downloadUrl };
};

// Function to handle file download
const handleFileDownload = async (s3Key: string, filename: string, chatbotId: string) => {
    if (!chatbotId) {
        message.error('Chatbot ID not available for download');
        return;
    }

    try {
        console.log('Downloading file:', filename, 'S3 Key:', s3Key);

        // Use the FileDownload Lambda (defaults to presigned URL)
        const downloadUrl = `${process.env.REACT_APP_API_HOST}/chatbot/files/${chatbotId}/${encodeURIComponent(s3Key)}`;

        const response = await axios.get(downloadUrl, {
            timeout: 30000,
            responseType: 'json'
        });

        if (response.status === 200 && response.data.results?.downloadUrl) {
            // Use presigned URL to trigger download
            const link = document.createElement('a');
            link.href = response.data.results.downloadUrl;
            link.download = filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`File "${filename}" downloaded successfully`);
            console.log('File download successful:', filename);
        }
    } catch (error: any) {
        console.error('File download error:', error);
        message.error(`Failed to download file: ${error.response?.data?.message || error.message}`);
    }
};

// Component to render clickable file messages
const FileMessage = ({ text, chatbotId, dynamicHeaderColor, dynamicPrimaryColor, dynamicIconColor }: {
    text: string;
    chatbotId: string;
    dynamicHeaderColor?: string;
    dynamicPrimaryColor?: string;
    dynamicIconColor?: string;
}) => {
    const fileInfo = parseFileInfo(text);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

            {/* File Card */}
            <div
                onClick={() => handleFileDownload(fileInfo.s3Key, fileInfo.filename, chatbotId)}
                style={{
                    backgroundColor: dynamicHeaderColor ? `${dynamicHeaderColor}08` : '#f8f9fa',
                    border: `1px solid ${dynamicHeaderColor ? `${dynamicHeaderColor}20` : '#e9ecef'}`,
                    borderRadius: '12px',
                    padding: '16px',
                    maxWidth: '320px',
                    boxShadow: `0px 4px 12px 0px ${dynamicHeaderColor ? `${dynamicHeaderColor}15` : 'rgba(0, 0, 0, 0.1)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ':hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0px 6px 16px 0px ${dynamicHeaderColor ? `${dynamicHeaderColor}25` : 'rgba(0, 0, 0, 0.15)'}`
                    }
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0px 6px 16px 0px ${dynamicHeaderColor ? `${dynamicHeaderColor}25` : 'rgba(0, 0, 0, 0.15)'}`;
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0px)';
                    e.currentTarget.style.boxShadow = `0px 4px 12px 0px ${dynamicHeaderColor ? `${dynamicHeaderColor}15` : 'rgba(0, 0, 0, 0.1)'}`;
                }}
            >
                {/* File Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FileTextOutlined style={{ color: dynamicHeaderColor || '#0066cc', fontSize: '20px' }} />
                    <div style={{ flex: 1 }}>
                        <Typography.Text strong style={{ color: '#000', fontSize: '14px', display: 'block' }}>
                            {fileInfo.filename}
                        </Typography.Text>
                        <Typography.Text style={{ color: '#8E8E93', fontSize: '12px' }}>
                            {fileInfo.size}
                        </Typography.Text>
                    </div>
                    <DownloadOutlined style={{ color: dynamicHeaderColor || '#0066cc', fontSize: '16px' }} />
                </div>
            </div>
        </div>
    );
};

// Reemplaza el texto y pone negritas y subrayado/itálica y links
// const renderFormattedText = (text: string, onArtifactClick?: (artifact: {content: string, language?: string}) => void, dynamicHeaderColor?: string) => {
const renderFormattedText = (text: string, onArtifactClick?: (artifact: { content: string, language?: string }) => void, dynamicHeaderColor?: string, onAcceptArtifactParam?: (artifact: { content: string, language?: string }) => void) => {
    // Detectar artifacts primero
    const artifactInfo = detectArtifacts(text);

    // primero escapa los caracteres especiales de HTML
    const escaped = artifactInfo.cleanText
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    // separa el texto en partes basado en los patrones de markdown
    const parts = escaped.split(/(\*\*.*?\*\*|_.*?_|\[.*?\]\(.*?\))/g);

    const formattedText = parts.map((part, i) => {
        // maneja el texto en negritas
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        // maneja el texto en subrayado/itálica
        if (part.startsWith('_') && part.endsWith('_')) {
            return <em key={i}>{part.slice(1, -1)}</em>;
        }
        // maneja los links
        const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
        if (linkMatch) {
            return (
                <a
                    key={i}
                    href={linkMatch[2]}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {linkMatch[1]}
                </a>
            );
        }
        // retorna el texto regular
        return <span key={i}>{part}</span>;
    });

    // Si hay artifact, agregar card
    if (artifactInfo.hasArtifact && onArtifactClick) {
        return (
            <>
                {formattedText}
                <Card
                    size="small"
                    style={{
                        marginTop: '12px',
                        cursor: 'pointer',
                        backgroundColor: dynamicHeaderColor ? dynamicHeaderColor + '10' : '#f8f9fa',
                        border: `1px solid ${dynamicHeaderColor ? dynamicHeaderColor + '30' : '#e9ecef'}`,
                        transition: 'all 0.3s ease'
                    }}
                    onClick={() => onArtifactClick({
                        content: artifactInfo.content,
                        language: artifactInfo.language
                    })}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FileTextOutlined style={{ color: dynamicHeaderColor || '#7E70B3' }} />
                            <Text strong style={{ color: dynamicHeaderColor || '#000000' }}>View Code Artifact</Text>
                            <Text type="secondary" style={{ color: (dynamicHeaderColor || '#666666') + '80' }}>({artifactInfo.language})</Text>
                        </div>
                        <ArrowRightOutlined style={{ color: dynamicHeaderColor || '#7E70B3', fontSize: '14px', paddingLeft: '10px' }} />
                    </div>
                </Card>
                {onAcceptArtifactParam && (
                    <Button
                        type="primary"
                        size="small"
                        style={{
                            marginTop: '8px',
                            backgroundColor: dynamicHeaderColor || '#7E70B3',
                            borderColor: dynamicHeaderColor || '#7E70B3',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onAcceptArtifactParam({
                                content: artifactInfo.content,
                                language: artifactInfo.language
                            });
                        }}
                    >
                        Accept
                    </Button>
                )}
            </>
        );
    }

    return formattedText;
};

export function Chatbot({ wsUrl, chatbotId, channelId, voiceWsUrl, urlApi, rabbitmqWebSocketUrl = 'wss://rabbitmq.dev.mind.creai.cloud/ws', isWeb = false, isPreview = false, primaryColor, headerColor, iconColor, botName, onArtifactAccept, autoAcceptArtifacts = true }: ChatbotProps): React.ReactElement {
    const params = useParams<{ chatbotId: string }>();
    chatbotId = chatbotId || params?.chatbotId || '0';
    const isOnPremise = chatbotId === '084e0206-ea8c-45d4-aa47-36ee29908fd3';

    const [inputText, setInputText] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    // Check URL params to determine if opened from preview button
    const urlParams = new URLSearchParams(window.location.search);
    const openFromPreview = urlParams.get('preview') === 'true';
    const [isMinimized, setIsMinimized] = useState<boolean>(openFromPreview || isPreview ? false : true);
    const [isConversationResolved, setIsConversationResolved] = useState<boolean>(false);
    const [hasShownGreeting, setHasShownGreeting] = useState<boolean>(() => {
        const storedGreetingShown = window.localStorage.getItem(`chatbot_greeting_shown_${chatbotId}`);
        if (storedGreetingShown !== null) {
            return JSON.parse(storedGreetingShown);
        }
        const storedMessages = window.localStorage.getItem(`chatbot_messages_${chatbotId}`);
        return storedMessages ? JSON.parse(storedMessages).length > 0 : false;
    });
    const [messages, setMessages] = useState<Message[]>(() => {
        const storedMessages = window.localStorage.getItem(`chatbot_messages_${chatbotId}`);
        return storedMessages ? JSON.parse(storedMessages) : [];
    });
    const [resetTimestamp, setResetTimestamp] = useState<number>(Date.now());
    const [chatbotConfiguration, setChatbotConfiguration] = useState<ChatbotConfiguration | null>(null);
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<any>(null);
    const [clientId, setClientId] = useState<string>(() => {
        const storedClientId = window.localStorage.getItem('chatbotClientId');
        if (storedClientId) {
            return storedClientId;
        }
        const newId = generateUUID();
        window.localStorage.setItem('chatbotClientId', newId);
        return newId;
    });

    // ⚠️ LEGACY: RabbitMQ connection - Only used for on-premise environment
    // This is legacy code for receiving error messages from RabbitMQ exchange (errorExchange)
    // It subscribes to RabbitMQ and forwards error messages via WebSocket to the widget
    // NOTE: This is NOT for normal user messages - those now use HTTP API
    // TODO: Evaluate if this is still needed or can be migrated to the new WebSocket event system
    const stompClientRef = useRef<Client | null>(null);
    const [rabbitMQAttempts, setRabbitMQAttempts] = useState<number>(0);
    const [isRabbitMQEnabled, setIsRabbitMQEnabled] = useState<boolean>(true);
    const rabbitMQTimeoutRef = useRef<any>(null);

    const computedChannelId = channelId || clientId;

    // 🎯 Initialize chatbot service (using useRef to avoid recreating on each render)
    const chatbotService = useRef(
        createChatbotService(
            urlApi,
            isOnPremise ? 'http://54.162.186.44:3001/chatbot' : undefined
        )
    );

    // Ref to track processed message IDs for deduplication
    const processedMessageIdsRef = useRef<Set<string>>(new Set());

    // Clean up processed message IDs periodically to prevent memory leak
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            // Keep only the last 100 message IDs to prevent unbounded growth
            if (processedMessageIdsRef.current.size > 100) {
                const idsArray = Array.from(processedMessageIdsRef.current);
                processedMessageIdsRef.current.clear();
                // Keep the most recent 50 IDs
                idsArray.slice(-50).forEach(id => processedMessageIdsRef.current.add(id));
            }
        }, 60000); // Clean up every minute

        return () => clearInterval(cleanupInterval);
    }, []);

    // 🎯 Handle WebSocket messages - extract message processing logic
    const handleWebSocketMessage = useCallback((messageData: any) => {
        // 🎯 Handle conversations channel - ONLY message.created events
        // Format: { event: "conversations", data: { type: "message.created", payload: {...} } }
        // Note: Conversation state events (resolved, assigned, etc.) are handled by the escalations system
        // and don't need to be processed by the widget
        if (messageData.event === 'conversations' && messageData.data) {
            const eventData = messageData.data;
            const eventType = eventData.type;
            const payload = eventData.payload || {};

            // Only handle message.created events - ignore all other conversation events
            if (eventType === 'message.created') {
                const sender = payload.sender;
                const messageContent = payload.content;
                const messageChannelId = payload.channelId || payload.conversationId;
                const messageId = payload.messageId || `msg-${Date.now()}-${sender?.type || 'unknown'}`;

                // Early validation
                if (!sender) {
                    return;
                }

                // Check if message content is empty or whitespace-only (tool identification case)
                const hasContent = messageContent && typeof messageContent === 'string' && messageContent.trim().length > 0;

                // Filter out system and user messages
                if (sender.type === 'system' || sender.id === 'system' || sender.type === 'user') {
                    return;
                }

                // Only process messages for THIS widget's channel
                if (messageChannelId !== computedChannelId) {
                    return;
                }

                // Accept ONLY messages from bot and agent
                const isFromBot = sender.type === 'bot';
                const isFromHumanAgent = sender.type === 'agent' || sender.type === 'human';

                if (!isFromBot && !isFromHumanAgent) {
                    return;
                }

                // Handle message updates: if messageId exists, update it; otherwise, create new
                const isExistingMessage = processedMessageIdsRef.current.has(messageId);

                if (isExistingMessage) {
                    // ✅ Update existing message with new content (streaming update)
                    // Only update if we have actual content
                    if (hasContent) {
                        setMessages((prev) => prev.map(msg =>
                            msg.id === messageId
                                ? { ...msg, text: messageContent, timestamp: Date.now() }
                                : msg
                        ));
                        // Progressive content will be updated by the useEffect
                        // Only disable typing indicator if content has actual text
                        setIsTyping(false);
                        setIsProcessing(false);
                    }
                    // If empty content, keep typing indicator active (tool execution in progress)
                    return;
                }

                // Mark as processed to prevent duplicates
                processedMessageIdsRef.current.add(messageId);

                // Only add message and disable typing if we have actual content
                if (hasContent) {
                    // ✅ Valid message with content - add it
                    const newMessage = {
                        text: messageContent,
                        isUser: false,
                        timestamp: Date.now(),
                        id: messageId
                    };

                    setMessages((prev) => [...prev, newMessage]);
                    // Initialize progressive content as empty string to trigger animation from start
                    setProgressiveContent((prev) => ({
                        ...prev,
                        [messageId]: ''
                    }));
                    // Disable typing indicator since we have content to show
                    setIsTyping(false);
                    setIsProcessing(false);
                } else {
                    // Empty message (likely tool identification) - keep typing indicator active
                    console.log('⚠️ Empty message received (likely tool identification) - keeping typing indicator active');
                }

                return;
            }

            // Ignore all other conversation events (resolved, assigned, state_changed, etc.)
            // These are handled by the escalations system and don't need widget processing
            return;
        }

        // 🎯 Handle real-time assistant data updates
        // Format: { event: "realtime-assistant", data: { emotion: {...}, objectives: {...}, activities: [...] } }
        if (messageData.event === 'realtime-assistant' && messageData.data) {
            try {
                const data: RealTimeAssistantData = messageData.data;
                setAssistantData(data);
                console.log('Real-time assistant data updated:', data);
            } catch (error) {
                console.error('Error processing real-time assistant data:', error);
            }
            return;
        }

        // Unknown event type - silently ignore to reduce console noise
    }, [computedChannelId]);

    // 🎯 Use WebSocket hook
    const { correlationId, sendMessage: sendWebSocketMessage, reconnect: reconnectWebSocket, disconnect: disconnectWebSocket } = useWidgetWebSocket({
        wsUrl,
        chatbotId: chatbotId || '',
        channelId: computedChannelId,
        onMessageReceived: handleWebSocketMessage
    });

    // Dynamic colors - use props if provided, otherwise fallback to chatbotConfiguration
    const dynamicPrimaryColor = primaryColor || chatbotConfiguration?.colorTitle || '#E3F571';
    const dynamicHeaderColor = headerColor || chatbotConfiguration?.colorMain || '#7E70B3';
    const dynamicIconColor = iconColor || chatbotConfiguration?.iconBgColor || '#000000';

    // Always use greeting from GET method (chatbotConfiguration), never from props
    const greetingFromConfig = chatbotConfiguration?.greeting;


    // Check if voice is enabled
    const isVoiceEnabled = chatbotConfiguration?.voiceLanguage && chatbotConfiguration.voiceLanguage !== 'off';

    // Check if assistant side panel is enabled
    const isAssistantSidePanelEnabled = chatbotConfiguration?.showRealTimeAssistant || true;

    // Always call the hook (to satisfy Rules of Hooks) but pass the disabled state
    const {
        connectionStatus: voiceStatus,
        chatMessages: voiceMessages,
        sessionInitialized,
        isStreaming: voiceStreaming,
        isUserListening,
        isAssistantThinking,
        connect: voiceConnect,
        disconnect: voiceDisconnect,
        requestMicrophoneAccess,
        startStreamingAudio,
        stopStreamingAudio,
        clearChatMessages
    } = useVoiceAgent({
        websocketUrl: voiceWsUrl,
        chatbotId,
        channelId: computedChannelId,
        disabled: !isVoiceEnabled,
        onAssistantDataReceived: (data: any) => {
            console.log('Chatbot received voice assistant data:', data);
            if (data) {
                setAssistantData(data);
            }
        }
    });
    const [voiceStartRequested, setVoiceStartRequested] = useState<boolean>(false);
    const [showVoiceAnimation, setShowVoiceAnimation] = useState<boolean>(false);
    const [isMicMuted, setIsMicMuted] = useState<boolean>(false);
    const [isDetailsCollapsed, setIsDetailsCollapsed] = useState<boolean>(false);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // Real-time Assistant Panel Data - Will be populated via WebSocket "realtime-assistant" event
    const [assistantData, setAssistantData] = useState<RealTimeAssistantData>({
        emotion: {
            percentage: 0
        },
        objectives: {
            progress: 0,
            items: []
        },
        activities: []
    });
    const [fileModalVisible, setFileModalVisible] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
    const [fileContent, setFileContent] = useState<string>('');
    const [showArtifactView, setShowArtifactView] = useState<boolean>(false);
    const [currentArtifact, setCurrentArtifact] = useState<{ content: string, language?: string } | null>(null);
    const [lastProcessedMessageId, setLastProcessedMessageId] = useState<string | null>(null);
    const editorRef = useRef<any>(null);
    const typingTimerRef = useRef<any>(null);
    const [shouldStartTimer, setShouldStartTimer] = useState<boolean>(false);

    // Progressive content display for streaming updates
    const [progressiveContent, setProgressiveContent] = useState<Record<string, string>>({});
    const progressiveTimersRef = useRef<Record<string, NodeJS.Timeout>>({});
    // Track initial messages to prevent animating them on load
    const initialMessageIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (isTyping && shouldStartTimer) {
            // Si se activa la escritura Y ya llegó el primer mensaje del bot, iniciar un temporizador de 8 segundos.
            typingTimerRef.current = setTimeout(() => {
                console.log('Se alcanzó el tiempo de espera de 8s para la escritura. Forzando la desactivación del indicador.');
                setIsTyping(false);
                setShouldStartTimer(false); // Reset flag
            }, 5000);
        } else if (!isTyping) {
            // Si se desactiva la escritura, limpiar el temporizador.
            if (typingTimerRef.current) {
                clearTimeout(typingTimerRef.current);
                typingTimerRef.current = null;
            }
        }

        // Función de limpieza para cuando el componente se desmonte o isTyping cambie.
        return () => {
            if (typingTimerRef.current) {
                clearTimeout(typingTimerRef.current);
            }
        };
    }, [isTyping, shouldStartTimer]);

    useEffect(() => {
        window.localStorage.setItem(`chatbot_messages_${chatbotId}`, JSON.stringify(messages));
    }, [messages, chatbotId]);

    // Initialize progressiveContent for existing messages on mount or after reset (no animation)
    useEffect(() => {
        // When messages exist and initialMessageIdsRef is empty (initial mount or after reset),
        // mark all existing messages and set their progressive content to full content
        if (messages.length > 0 && initialMessageIdsRef.current.size === 0) {
            const initialIds = new Set<string>();
            const initialProgressive: Record<string, string> = {};

            messages.forEach((msg) => {
                if (msg.id && !msg.isUser) {
                    initialIds.add(msg.id);
                    // Set full content immediately for existing messages (no animation)
                    initialProgressive[msg.id] = msg.text;
                }
            });

            initialMessageIdsRef.current = initialIds;
            setProgressiveContent(initialProgressive);
        }
    }, [messages]); // Run when messages change and ref is empty (initial load or reset)

    // Progressive content update effect - animates text updates for streaming messages
    useEffect(() => {
        messages.forEach((msg) => {
            if (!msg.id || msg.isUser) return;

            const targetContent = msg.text;
            const currentProgressive = progressiveContent[msg.id];
            const isInitialMessage = initialMessageIdsRef.current.has(msg.id);

            // For initial messages, always use full content (no animation)
            if (isInitialMessage) {
                if (currentProgressive !== targetContent) {
                    setProgressiveContent(prev => ({
                        ...prev,
                        [msg.id!]: targetContent
                    }));
                }
                return;
            }

            // For new messages (from WebSocket), initialize as empty string to trigger animation
            const currentContent = currentProgressive === undefined ? '' : currentProgressive;

            // If this is a new message, initialize progressive content as empty string
            if (currentProgressive === undefined) {
                setProgressiveContent(prev => ({
                    ...prev,
                    [msg.id!]: ''
                }));
            }

            // If content is already fully displayed, no need to animate
            if (currentContent === targetContent) {
                return;
            }

            // Only animate if target is longer than current (streaming update or initial message)
            if (targetContent.length <= currentContent.length) {
                // Content was shortened or same length - update immediately
                setProgressiveContent(prev => ({
                    ...prev,
                    [msg.id!]: targetContent
                }));
                return;
            }

            // Clear any existing timer for this message
            if (progressiveTimersRef.current[msg.id]) {
                clearInterval(progressiveTimersRef.current[msg.id]);
            }

            // Start progressive update from current position (0 for new messages, current length for updates)
            let currentIndex = currentContent.length;
            const updateInterval = setInterval(() => {
                if (currentIndex < targetContent.length) {
                    // Add characters progressively (3 chars per update for smooth animation)
                    const nextChunk = targetContent.substring(0, Math.min(currentIndex + 3, targetContent.length));
                    setProgressiveContent(prev => ({
                        ...prev,
                        [msg.id!]: nextChunk
                    }));
                    currentIndex = nextChunk.length;
                } else {
                    // Animation complete
                    clearInterval(updateInterval);
                    delete progressiveTimersRef.current[msg.id!];
                    setProgressiveContent(prev => ({
                        ...prev,
                        [msg.id!]: targetContent
                    }));
                }
            }, 20); // Update every 20ms for smooth animation (adjust for speed)

            progressiveTimersRef.current[msg.id] = updateInterval;
        });

        // Cleanup function
        return () => {
            Object.values(progressiveTimersRef.current).forEach(timer => clearInterval(timer));
        };
    }, [messages]); // Only depend on messages, not progressiveContent to avoid loops

    useEffect(() => {
        window.localStorage.setItem(`chatbot_greeting_shown_${chatbotId}`, JSON.stringify(hasShownGreeting));
    }, [hasShownGreeting, chatbotId]);

    useEffect(() => {
        // WebSocket connection is handled automatically by useWidgetWebSocket hook
        // Solo conectar a RabbitMQ si estamos en entorno on-premise
        if (isOnPremise && isRabbitMQEnabled) {
            connectToRabbitMQ();
        }

        return () => {
            // WebSocket disconnection is handled by useWidgetWebSocket hook
            // WebSocket cleanup is handled by useWidgetWebSocket hook
            if (stompClientRef.current) {
                stompClientRef.current.deactivate();
            }
            clearTimeout(rabbitMQTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, computedChannelId, isOnPremise, isRabbitMQEnabled]);

    // Separate useEffect for chatbot config to avoid infinite loops
    useEffect(() => {
        getChatbotConfig();
    }, [chatbotId]);

    useEffect(() => {
        if (voiceStartRequested && sessionInitialized) {
            startStreamingAudio();
        }
    }, [voiceStartRequested, sessionInitialized, startStreamingAudio]);

    useEffect(() => {
        if (voiceStreaming) {
            setVoiceStartRequested(false);
        }
    }, [voiceStreaming]);

    useEffect(() => {
        if (!isMinimized && inputRef.current && inputRef.current.input) {
            inputRef.current.input.focus();
        }
    }, [isMinimized]);

    useEffect(() => {
        if (messagesContainerRef.current) {
            // Use requestAnimationFrame to ensure DOM has updated before scrolling
            requestAnimationFrame(() => {
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                }
            });
        }
    }, [messages, voiceMessages, isTyping, isUserListening, isAssistantThinking]);

    // Separate effect for progressive content updates during streaming
    // Uses throttling to avoid excessive scroll updates
    // Only auto-scrolls if user is near the bottom (within 100px) to avoid interrupting reading
    useEffect(() => {
        if (messagesContainerRef.current && Object.keys(progressiveContent).length > 0) {
            // Throttle scroll updates to avoid excessive re-renders
            const scrollTimeout = setTimeout(() => {
                if (messagesContainerRef.current) {
                    const container = messagesContainerRef.current;
                    const scrollHeight = container.scrollHeight;
                    const scrollTop = container.scrollTop;
                    const clientHeight = container.clientHeight;
                    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

                    // Only auto-scroll if user is near the bottom (within 100px)
                    // This prevents interrupting users who are reading previous messages
                    if (distanceFromBottom < 100) {
                        requestAnimationFrame(() => {
                            if (messagesContainerRef.current) {
                                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                            }
                        });
                    }
                }
            }, 50); // Update scroll every 50ms during streaming (throttled)

            return () => clearTimeout(scrollTimeout);
        }
    }, [progressiveContent]);

    // Detectar nuevos artifacts y abrir automáticamente o llamar callback
    useEffect(() => {
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];

            // Solo procesar mensajes del asistente que no sean del usuario
            if (!lastMessage.isUser && lastMessage.id && lastMessage.id !== lastProcessedMessageId) {
                const artifactInfo = detectArtifacts(lastMessage.text);

                if (artifactInfo.hasArtifact) {
                    // If autoAcceptArtifacts is true AND onArtifactAccept callback is provided, call it immediately
                    if (autoAcceptArtifacts && onArtifactAccept) {
                        console.log('Auto-accepting artifact:', artifactInfo);
                        onArtifactAccept({
                            content: artifactInfo.content,
                            language: artifactInfo.language
                        });
                        setLastProcessedMessageId(lastMessage.id);
                    } else if (!autoAcceptArtifacts && onArtifactAccept) {
                        // If autoAcceptArtifacts is false, don't auto-call but the Accept button will be shown
                        // The button will call onArtifactAccept when clicked
                        console.log('Artifact detected, waiting for user to click Accept button');
                        setLastProcessedMessageId(lastMessage.id);
                    } else {
                        // Otherwise, open the artifact view modal
                        setCurrentArtifact({
                            content: artifactInfo.content,
                            language: artifactInfo.language
                        });
                        setShowArtifactView(true);
                        setLastProcessedMessageId(lastMessage.id);
                    }
                }
            }
        }
    }, [messages, lastProcessedMessageId, onArtifactAccept, autoAcceptArtifacts]);

    // sendWebSocketMessage is now provided by useWidgetWebSocket hook

    // ⚠️ LEGACY: RabbitMQ connection functions
    // These functions are ONLY used for on-premise environment to receive ERROR messages
    // from RabbitMQ exchange (errorExchange). This is NOT for normal user messages.
    // Normal user messages now use HTTP API (chatbotService.sendMessage).
    // This legacy code forwards error alerts from RabbitMQ to the widget via WebSocket.
    // TODO: Evaluate if this can be migrated to the new WebSocket event system or removed.
    const disableRabbitMQ = () => {
        setIsRabbitMQEnabled(false);
        if (stompClientRef.current) {
            stompClientRef.current.deactivate();
            stompClientRef.current = null;
        }
        clearTimeout(rabbitMQTimeoutRef.current);
        setMessages((prev) => [
            ...prev,
            {
                text: "RabbitMQ connection disabled after multiple failed attempts. Chatbot will continue in proactive mode.",
                isUser: false,
                timestamp: Date.now()
            }
        ]);
    };

    const connectToRabbitMQ = () => {
        if (!isOnPremise || !isRabbitMQEnabled) return;

        if (rabbitMQAttempts >= 5) {
            disableRabbitMQ();
            return;
        }

        try {
            console.log(`Attempting RabbitMQ connection. Attempt ${rabbitMQAttempts + 1} of 5`);

            const client = new Client({
                brokerURL: rabbitmqWebSocketUrl,
                connectHeaders: {
                    login: 'user',
                    passcode: 'pass',
                },
                debug: function (str) {
                    console.log('STOMP: ' + str);
                },
                reconnectDelay: 0, // Desactivamos la reconexión automática
                heartbeatIncoming: 5000,
                heartbeatOutgoing: 5000,
            });

            client.onConnect = function () {
                console.log('Connected to RabbitMQ via STOMP');
                setRabbitMQAttempts(0);

                const routingKey = `user.testuser123`;
                client.subscribe(`/exchange/errorExchange/${routingKey}`, function (message) {
                    try {
                        if (message.body) {
                            const event = JSON.parse(message.body);
                            console.log('Message received from RabbitMQ:', event);

                            const payload = {
                                action: 'errorMessage',
                                from: clientId,
                                message: event.message,
                                chatbotId: chatbotId || '0',
                                channelId: computedChannelId
                            };

                            const sentSuccessfully = sendWebSocketMessage(payload);

                            // Marcar que ya se mostró el greeting para evitar que aparezca
                            setHasShownGreeting(true);

                            // Limpiar la conversación para que quede en blanco
                            setMessages([]);
                            window.localStorage.setItem(`chatbot_messages_${chatbotId}`, JSON.stringify([]));

                            // Abrir el popup
                            setIsMinimized(false);

                            if (!sentSuccessfully) {
                                setMessages((prev) => [
                                    ...prev,
                                    {
                                        text: "Error: Could not forward alert to server. Will retry when connection is restored.",
                                        isUser: false,
                                        timestamp: Date.now()
                                    }
                                ]);
                            }
                        }
                    } catch (error) {
                        console.error('Error processing RabbitMQ message:', error);
                    }
                });
            };

            client.onStompError = function (frame) {
                console.error('STOMP error:', frame.headers.message);
                const nextAttempt = rabbitMQAttempts + 1;
                setRabbitMQAttempts(nextAttempt);

                if (nextAttempt >= 5) {
                    disableRabbitMQ();
                } else {
                    clearTimeout(rabbitMQTimeoutRef.current);
                    rabbitMQTimeoutRef.current = setTimeout(connectToRabbitMQ, 5000);
                }
            };

            stompClientRef.current = client;
            client.activate();

        } catch (err) {
            console.error('Failed to connect to RabbitMQ:', err);
            const nextAttempt = rabbitMQAttempts + 1;
            setRabbitMQAttempts(nextAttempt);

            if (nextAttempt >= 5) {
                disableRabbitMQ();
            } else {
                clearTimeout(rabbitMQTimeoutRef.current);
                rabbitMQTimeoutRef.current = setTimeout(connectToRabbitMQ, 5000);
            }
        }
    };

    const handleResetConfirmation = () => {
        Modal.confirm({
            title: 'Are you sure you want to restart this conversation?',
            content: 'This will delete the current conversation history. You\'ll start a new chat from scratch, and all previous messages will be lost.',
            okText: 'Restart Conversation',
            cancelText: 'Cancel',
            centered: true,
            okButtonProps: {
                style: {
                    backgroundColor: 'var(--ant-colorPrimary)',
                    color: 'var(--ant-colorText)',
                },
            },
            onOk() {
                console.log('Reset: Current computedChannelId:', computedChannelId);

                // Stop voice streaming and disconnect if active
                if (voiceStreaming) {
                    stopStreamingAudio();
                }
                voiceDisconnect();
                clearChatMessages();

                // Set reset timestamp as fallback to filter out old voice messages
                const resetTime = Date.now();
                setResetTimestamp(resetTime);

                // Reset text messages and greeting state
                const newMessages = [{ text: greetingFromConfig || 'Hello! How can I help you?', isUser: false, timestamp: resetTime }];
                setMessages(newMessages);
                setHasShownGreeting(true);
                window.localStorage.setItem(`chatbot_messages_${chatbotId}`, JSON.stringify(newMessages));

                // Clear progressive content and timers
                setProgressiveContent({});
                Object.values(progressiveTimersRef.current).forEach(timer => clearInterval(timer));
                progressiveTimersRef.current = {};
                processedMessageIdsRef.current.clear();
                // Reset initial message IDs so the greeting message is marked as initial (no animation)
                initialMessageIdsRef.current.clear();

                // Reset processing and typing states
                setIsTyping(false);
                setIsProcessing(false);
                setIsConversationResolved(false);

                // Generate new client ID to ensure fresh session
                const newClientId = generateUUID();
                window.localStorage.setItem('chatbotClientId', newClientId);
                setClientId(newClientId);

                console.log('Reset: New clientId generated:', newClientId);
                console.log('Reset: New computedChannelId will be:', channelId || newClientId);

                // Close current WebSocket cleanly to trigger reconnection with new clientId
                // Disconnect WebSocket (handled by hook)
                disconnectWebSocket();

                // Manually trigger reconnection with new clientId after a brief delay
                setTimeout(() => {
                    reconnectWebSocket();
                }, 100);
            }
        });
    };

    const getChatbotConfig = async () => {
        try {
            const config = await chatbotService.current.getChatbotConfig(chatbotId.toString(), isOnPremise);
            setChatbotConfiguration(config);

            // Solo mostrar greeting si no hay mensajes Y no se ha mostrado el greeting antes
            if (messages.length === 0 && !hasShownGreeting && config.greeting) {
                setMessages([{ text: config.greeting, isUser: false, timestamp: Date.now() }]);
                setHasShownGreeting(true);
            }
        } catch (error) {
            console.error("Error fetching chatbot configuration:", error);
        }
    };

    // WebSocket connection is now handled by useWidgetWebSocket hook

    const handleSendMessage = async () => {
        if (!inputText.trim()) return;

        // Save message text before clearing input
        const messageToSend = inputText.trim();

        // Clear input IMMEDIATELY to improve UX
        setInputText('');

        // Set processing states BEFORE sending
        setIsProcessing(true);
        setIsTyping(true);

        const userMessage = {
            text: messageToSend,
            isUser: true,
            timestamp: Date.now(),
            id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };
        setMessages((prev) => [...prev, userMessage]);

        // 📤 Send message via HTTP API with correlationId
        try {
            await chatbotService.current.sendMessage({
                message: messageToSend,
                chatbotId: chatbotId.toString(),
                channelId: computedChannelId,
                correlationId: correlationId, // 🆔 Propagate correlationId for end-to-end tracing
                from: computedChannelId,
                product: 'web'
            });

            console.log('✅ Message sent successfully via HTTP API', {
                correlationId,
                channelId: computedChannelId
            });

            // Focus input after sending (small delay to ensure DOM updated)
            setTimeout(() => {
                if (inputRef.current && inputRef.current.input) {
                    inputRef.current.input.focus();
                }
            }, 50);
        } catch (error) {
            console.error('❌ Failed to send message via HTTP API:', error);
            // On error, restore text and deactivate states
            setInputText(messageToSend);
            setIsTyping(false);
            setIsProcessing(false);
        }

        // NOTE: isProcessing and isTyping will remain true until bot response arrives
        // via WebSocket in handleWebSocketMessage, where they will be set to false
    };

    const handleVoiceButtonClick = async () => {
        if (voiceStreaming) {
            stopStreamingAudio();
            voiceDisconnect();
            setShowVoiceAnimation(false);
            setIsDetailsCollapsed(true); // Reset panel state when closing voice
            return;
        }

        setShowVoiceAnimation(true);
        setIsDetailsCollapsed(true); // Start with panel collapsed when opening voice
        setVoiceStartRequested(true);
        voiceConnect();
        await requestMicrophoneAccess();
    };

    const handleCloseVoiceAnimation = () => {
        setShowVoiceAnimation(false);
        setShowArtifactView(false); // Ensure artifact view is also closed
        setIsMicMuted(false);
        setVoiceStartRequested(false); // Reset this state to re-enable buttons
        setIsDetailsCollapsed(true); // Reset panel state
        if (voiceStreaming) {
            stopStreamingAudio();
            voiceDisconnect();
        }
        if (videoRef.current) {
            videoRef.current.pause();
        }
    };

    const handleMicToggle = () => {
        setIsMicMuted(!isMicMuted);
        if (videoRef.current) {
            if (isMicMuted) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        }
    };

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            setFileContent(content);
        };
        reader.readAsText(file);
        return false; // Prevent auto upload
    };

    const handleFileUpload = async () => {
        if (!selectedFile) {
            message.error('Please select a file first');
            return;
        }

        // TODO: Implement file upload logic here
        message.info('File upload functionality will be implemented soon');
        setFileModalVisible(false);
        setSelectedFile(null);
        setFileContent('');
    };

    const handleArtifactClick = (artifact: { content: string, language?: string }) => {
        setCurrentArtifact(artifact);
        setShowArtifactView(true);
    };

    const handleEditorDidMount = (editor: any) => {
        editorRef.current = editor;
        // Pequeño delay para asegurar que el editor esté completamente montado
        setTimeout(() => {
            // Verificar si el modelo existe y tiene contenido
            const model = editor.getModel();
            if (model) {
                console.log('Total lines in editor:', model.getLineCount());
                console.log('Editor content:', model.getValue());

                // Posicionar el scroll al inicio del documento (primera línea)
                editor.setPosition({ lineNumber: 1, column: 1 });
                editor.revealLine(1, 0); // 0 = scrollType: immediate
                editor.setScrollTop(0); // Forzar scroll al top

                // Forzar re-layout del editor
                editor.layout();
            }
        }, 100);
    };

    const handleCloseArtifactView = () => {
        setShowArtifactView(false);
        setCurrentArtifact(null);
    };

    const toggleMinimize = () => setIsMinimized(!isMinimized);

    const messageStyle = (isUser: boolean): React.CSSProperties => ({
        backgroundColor: isUser ? '#F7F7F665' : '#FFFFFF',
        padding: '12px 16px',
        borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
        maxWidth: '100%',
        display: 'inline-block',
        wordWrap: 'break-word',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        border: isUser ? 'none' : '1px solid #E5E5EA',
        minWidth: 'fit-content'
    });

    const messageTextStyle = (isUser: boolean): React.CSSProperties => ({
        color: isUser ? '#000000' : '#000000',
        textAlign: 'left',
        flex: 1,
        whiteSpace: 'pre-line',
        fontSize: '14px',
        lineHeight: '20px',
        fontFamily: '"DM Sans", sans-serif'
    });

    const TypingDots = () => {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
            }}>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
            </div>
        );
    };

    if (!chatbotConfiguration) {
        return <div />;
    }

    return (
        <div
            className={`${isWeb ? 'chatbot-web-container' : 'chatbot-chat-container'} ${showVoiceAnimation && !isDetailsCollapsed ? 'panel-open' : ''}`}
            style={{
                '--secondary-color': dynamicHeaderColor,
                ...(!isWeb ? {
                    ...(isPreview && {
                        position: 'static',
                        bottom: 'auto',
                        right: 'auto'
                    })
                } : {})
            } as React.CSSProperties}
        >
            {!isWeb && isMinimized ? (
                <Button
                    type="primary"
                    shape="circle"
                    icon={<MessageOutlined style={{ color: dynamicIconColor }} />}
                    size="large"
                    onClick={toggleMinimize}
                    style={{
                        backgroundColor: dynamicPrimaryColor,
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 12,
                    }}
                />
            ) : (
                <div style={{ position: 'relative', width: '100%' }}>
                    <Layout className={isWeb ? 'chatbot-web-layout' : ''} style={!isWeb ? {
                        border: '1px solid #ddd',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        width: '100%',
                        height: '570px',
                        transform: 'translateY(-55px)',
                        backgroundColor: 'var(--ant-colorContainer)',
                        display: 'flex',
                        flexDirection: (showVoiceAnimation && !isDetailsCollapsed) ? 'row' : 'column'
                    } : {
                        border: '1px solid #ddd',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'var(--ant-colorContainer)',
                        display: 'flex',
                        flexDirection: (showVoiceAnimation && !isDetailsCollapsed) ? 'row' : 'column'
                    }}>
                        {!showVoiceAnimation && !showArtifactView && (
                            <Header style={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--ant-paddingLG)',
                                height: 'auto',
                                borderBottom: '1px solid var(--ant-colorSplit)'
                            }}>
                                {!isWeb && (
                                    <Button
                                        type="link"
                                        icon={<LeftOutlined style={{ color: '#000000' }} />}
                                        onClick={toggleMinimize}
                                        style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--ant-colorSplit)' }}
                                    />
                                )}
                                <Text className="chatbot-title" style={isWeb ? { marginLeft: '16px' } : {}}>{chatbotConfiguration?.botName || botName || 'Assistant'}</Text>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'restart',
                                                label: 'Restart conversation',
                                                icon: <ReloadOutlined />,
                                                onClick: handleResetConfirmation
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <Button
                                        type="link"
                                        icon={<EllipsisOutlined style={{ color: '#000000' }} />}
                                        className="chatbot-dropdown-trigger"

                                    />
                                </Dropdown>
                            </Header>
                        )}
                        <Content ref={messagesContainerRef}
                            style={{
                                padding: (showVoiceAnimation || showArtifactView) ? '0' : '16px',
                                overflow: (showVoiceAnimation || showArtifactView) ? 'hidden' : 'auto',
                                backgroundColor: (showVoiceAnimation || showArtifactView) ? '#ffffff' : chatbotConfiguration.colorBg,
                                flex: 1,
                                display: (showVoiceAnimation && !isDetailsCollapsed) ? 'flex' : 'block',
                                flexDirection: (showVoiceAnimation && !isDetailsCollapsed) ? 'row' : undefined
                            }}>
                            {showVoiceAnimation ? (
                                /* Voice Mode - Side by Side Layout */
                                <>
                                    {/* Left Panel - Voice Animation (451px) */}
                                    <div
                                        style={{
                                            width: !isDetailsCollapsed ? '451px' : '100%',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRight: !isDetailsCollapsed ? '1px solid rgba(33, 33, 47, 0.15)' : 'none',
                                            flexShrink: 0
                                        }}
                                    >
                                        {/* Voice Mode Header */}
                                        <Header style={{
                                            backgroundColor: '#ffffff',
                                            borderBottom: '1px solid rgba(33, 33, 47, 0.15)',
                                            padding: '24px',
                                            height: '87px',
                                            minHeight: '87px',
                                            maxHeight: '87px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end'
                                        }}>
                                            {/* Arrow Toggle Button - Only show if assistant side panel is enabled */}
                                            {isAssistantSidePanelEnabled && (
                                                <Button
                                                    type="text"
                                                    icon={
                                                        <img
                                                            src={arrowUpIcon}
                                                            alt="toggle details"
                                                            style={{
                                                                width: '16px',
                                                                height: '16px',
                                                                transform: isDetailsCollapsed ? 'rotate(180deg)' : 'none',
                                                                transition: 'transform 0.3s ease',
                                                            }}
                                                        />
                                                    }
                                                    onClick={() => setIsDetailsCollapsed(!isDetailsCollapsed)}
                                                    style={{
                                                        backgroundColor: '#f2f2f3',
                                                        borderRadius: '8px',
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: 0,
                                                        minWidth: '24px',
                                                        marginLeft: 'auto'
                                                    }}
                                                    title={isDetailsCollapsed ? 'Expand details panel' : 'Collapse details panel'}
                                                />
                                            )}
                                        </Header>

                                        {/* Voice Animation */}
                                        <div
                                            className="voice-animation-container"
                                            style={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            {/* Video Container */}
                                            <div className="voice-animation-video-container">
                                                <video
                                                    ref={videoRef}
                                                    src={chatbotAnimation}
                                                    autoPlay
                                                    loop
                                                    muted
                                                    className="voice-animation-video"
                                                />
                                            </div>

                                            {/* Control Buttons */}
                                            <div className="voice-animation-controls">
                                                <Button
                                                    type="default"
                                                    shape="circle"
                                                    icon={<CloseOutlined />}
                                                    onClick={handleCloseVoiceAnimation}
                                                    size="large"
                                                    className="voice-control-button"
                                                />
                                                <Button
                                                    type="default"
                                                    shape="circle"
                                                    icon={<AudioOutlined style={{
                                                        color: isMicMuted ? '#ffffff' : undefined
                                                    }} />}
                                                    onClick={handleMicToggle}
                                                    size="large"
                                                    className={`voice-control-button ${isMicMuted ? 'muted' : ''}`}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {isAssistantSidePanelEnabled && (
                                        <RealTimeAssistantPanel
                                            data={assistantData}
                                            isVisible={!isDetailsCollapsed}
                                        />
                                    )}
                                </>
                            ) : showArtifactView ? (
                                /* Artifact View Screen - Full Layout */
                                <div className="artifact-view-container" style={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: '#ffffff'
                                }}>
                                    {/* Header with close button */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '16px 24px',
                                        borderBottom: `1px solid ${dynamicHeaderColor}20`,
                                        backgroundColor: dynamicHeaderColor + '10'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <FileTextOutlined style={{ color: dynamicHeaderColor, fontSize: '18px' }} />
                                            <Text strong style={{ fontSize: '16px', color: dynamicHeaderColor }}>
                                                Code Artifact
                                            </Text>
                                            {currentArtifact?.language && (
                                                <div style={{
                                                    padding: '4px 8px',
                                                    backgroundColor: dynamicPrimaryColor,
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    color: dynamicIconColor,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {currentArtifact.language}
                                                </div>
                                            )}
                                        </div>
                                        <Button
                                            type="default"
                                            shape="circle"
                                            icon={<CloseOutlined style={{ color: dynamicHeaderColor }} />}
                                            onClick={handleCloseArtifactView}
                                            size="large"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: `1px solid ${dynamicHeaderColor}30`,
                                                backgroundColor: dynamicHeaderColor + '05'
                                            }}
                                        />
                                    </div>

                                    {/* Code content */}
                                    <div style={{
                                        flex: 1,
                                        padding: '16px',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        <div style={{
                                            flex: 1,
                                            border: `1px solid ${dynamicHeaderColor}20`,
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: `0px 4px 12px 0px ${dynamicHeaderColor}20`
                                        }}>
                                            <Editor
                                                height="100%"
                                                language={currentArtifact?.language || 'plaintext'}
                                                value={currentArtifact?.content || ''}
                                                theme="vs"
                                                onMount={handleEditorDidMount}
                                                options={{
                                                    readOnly: true,
                                                    minimap: { enabled: false },
                                                    scrollBeyondLastLine: false,
                                                    fontSize: 14,
                                                    lineNumbers: 'on',
                                                    lineNumbersMinChars: 3,
                                                    renderLineHighlight: 'none',
                                                    roundedSelection: false,
                                                    scrollbar: {
                                                        vertical: 'auto',
                                                        horizontal: 'auto',
                                                        verticalScrollbarSize: 12,
                                                        horizontalScrollbarSize: 12,
                                                        alwaysConsumeMouseWheel: false
                                                    },
                                                    wordWrap: 'on',
                                                    automaticLayout: true,
                                                    mouseWheelZoom: false,
                                                    smoothScrolling: true,
                                                    cursorBlinking: 'solid',
                                                    glyphMargin: false,
                                                    folding: false,
                                                    overviewRulerBorder: false,
                                                    overviewRulerLanes: 0,
                                                    hideCursorInOverviewRuler: true
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Date header - centered */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginBottom: '24px',
                                        paddingTop: '8px'
                                    }}>
                                        <Text style={{
                                            fontSize: '12px',
                                            color: 'var(--ant-colorText)',
                                            fontFamily: '"DM Sans", sans-serif',
                                            padding: '4px',
                                            fontWeight: 400,
                                            lineHeight: '20px'
                                        }}>
                                            {new Date().toLocaleDateString('es-ES', {
                                                day: 'numeric',
                                                month: 'long'
                                            })}
                                        </Text>
                                    </div>
                                    {(() => {
                                        // Merge text and voice messages, excluding system messages
                                        const textMessages = messages.map((msg, idx) => ({
                                            ...msg,
                                            id: msg.id || `text-${idx}`,
                                            timestamp: msg.timestamp || 0,
                                            content: msg.text,
                                            role: msg.isUser ? 'USER' : 'ASSISTANT',
                                            isFromText: true
                                        }));

                                        const filteredVoiceMessages = voiceMessages
                                            .filter(msg => msg.role !== 'SYSTEM')
                                            .filter(msg => {
                                                // Filter out voice messages that are older than the reset timestamp
                                                const messageTime = msg.timestamp ? msg.timestamp.getTime() : 0;
                                                return messageTime >= resetTimestamp;
                                            })
                                            .map(msg => ({
                                                ...msg,
                                                timestamp: msg.timestamp || Date.now(),
                                                isFromText: false
                                            }));

                                        const allMessages = [...textMessages, ...filteredVoiceMessages]
                                            .sort((a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0));

                                        return allMessages.map((msg) => (
                                            <div key={msg.id} style={{
                                                display: 'flex',
                                                justifyContent: msg.role === 'USER' ? 'flex-end' : 'flex-start',
                                                marginBottom: '12px'
                                            }}>
                                                {msg.role === 'USER' ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '70%' }}>
                                                        <div style={messageStyle(true)}>
                                                            <Text style={messageTextStyle(true)}>
                                                                {msg.isFromText ? renderFormattedText(msg.content, handleArtifactClick, dynamicHeaderColor, onArtifactAccept) : msg.content}
                                                            </Text>
                                                        </div>
                                                        <Text style={{
                                                            fontSize: '11px',
                                                            color: '#8E8E93',
                                                            fontFamily: '"DM Sans", sans-serif',
                                                            marginTop: '4px',
                                                            marginRight: '8px'
                                                        }}>
                                                            {msg.timestamp ?
                                                                (Date.now() - Number(msg.timestamp) < 60000 ? 'Just now' :
                                                                    new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
                                                                : 'Just now'
                                                            }
                                                        </Text>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '70%' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                            <Text style={{
                                                                fontSize: '12px',
                                                                color: '#8E8E93',
                                                                fontWeight: 500
                                                            }}>
                                                                Virtual Agent
                                                            </Text>
                                                        </div>
                                                        <div style={messageStyle(false)}>
                                                            <Text style={messageTextStyle(false)}>
                                                                {msg.isFromText ?
                                                                    (isFileUploadMessage(msg.content) ?
                                                                        <FileMessage
                                                                            text={msg.content}
                                                                            chatbotId={chatbotId}
                                                                            dynamicHeaderColor={dynamicHeaderColor}
                                                                            dynamicPrimaryColor={dynamicPrimaryColor}
                                                                            dynamicIconColor={dynamicIconColor}
                                                                        /> :
                                                                        renderFormattedText(
                                                                            msg.id && progressiveContent[msg.id] !== undefined
                                                                                ? progressiveContent[msg.id]
                                                                                : msg.content,
                                                                            handleArtifactClick,
                                                                            dynamicHeaderColor,
                                                                            onArtifactAccept
                                                                        )
                                                                    ) :
                                                                    (msg.id && progressiveContent[msg.id] !== undefined
                                                                        ? progressiveContent[msg.id]
                                                                        : msg.content)
                                                                }
                                                            </Text>
                                                        </div>
                                                        <Text style={{
                                                            fontSize: '11px',
                                                            color: '#8E8E93',
                                                            fontFamily: '"DM Sans", sans-serif',
                                                            marginTop: '4px',
                                                            marginLeft: '8px'
                                                        }}>
                                                            {msg.timestamp ?
                                                                (Date.now() - Number(msg.timestamp) < 60000 ? 'Just now' :
                                                                    new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
                                                                : 'Just now'
                                                            }
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                        ));
                                    })()}

                                    {isTyping && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '70%' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                    <Text style={{
                                                        fontSize: '12px',
                                                        color: '#8E8E93',
                                                        fontFamily: '"DM Sans", sans-serif',
                                                        fontWeight: 500
                                                    }}>
                                                        Assistant
                                                    </Text>
                                                </div>
                                                <div style={{
                                                    ...messageStyle(false),
                                                    maxWidth: '100px',
                                                    minWidth: '60px'
                                                }}>
                                                    <TypingDots />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </Content>
                        {!showVoiceAnimation && !showArtifactView && (
                            <Footer
                                key="chatbot-footer"
                                style={{
                                    padding: '24px',
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '12px',
                                }}>
                                <div className="chatbot-input-wrapper" style={{
                                    boxShadow: `0px 20px 80px 0px ${dynamicHeaderColor}80, 0px 12px 48px 0px ${dynamicHeaderColor}66, 0px 6px 24px 0px ${dynamicHeaderColor}4D`
                                }}>
                                    <Input
                                        ref={inputRef}
                                        placeholder={isConversationResolved ? "This conversation has been resolved" : "Type a message..."}
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        onPressEnter={(e) => {
                                            // Prevent sending if processing or typing
                                            if (!isProcessing && !isTyping && !voiceStreaming && !voiceStartRequested && !isUserListening && !isAssistantThinking && !isConversationResolved && inputText.trim()) {
                                                handleSendMessage();
                                            }
                                        }}
                                        style={{
                                            flex: 1,
                                            borderRadius: '24px',
                                            border: '1px solid #E5E5EA',
                                            backgroundColor: '#0000000f !important',
                                            padding: '12px 16px',
                                            opacity: (isProcessing || isTyping || isConversationResolved) ? 0.6 : 1
                                        }}
                                        disabled={isProcessing || isTyping || voiceStreaming || voiceStartRequested || isUserListening || isAssistantThinking || isConversationResolved}
                                    />
                                    {/* <Button
                            shape="circle"
                            icon={<PaperClipOutlined />}
                            onClick={() => setFileModalVisible(true)}
                            size="large"
                            className="paperclip-button"
                            disabled={isConversationResolved}
                            style={{
                                width: '48px',
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                borderRadius: '12px',
                                border: '1px solid #E5E5EA',
                                backgroundColor: '#0000000f',
                                transition: 'all 0.3s ease',
                                opacity: isConversationResolved ? 0.6 : 1
                            }}
                        /> */}
                                    {isVoiceEnabled && !inputText.trim() && (
                                        <Button
                                            shape="circle"
                                            icon={
                                                <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 13.3334V6.66675M8 13.3334V2.66675M4 13.3334V9.33341" stroke="currentColor" strokeOpacity="0.88" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            }
                                            onClick={handleVoiceButtonClick}
                                            disabled={isProcessing || (voiceStartRequested && !voiceStreaming) || isConversationResolved}
                                            size="large"
                                            className="voice-button"
                                            style={{
                                                width: '48px',
                                                height: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                borderRadius: '12px',
                                                border: '1px solid #E5E5EA',
                                                backgroundColor: '#0000000f',
                                                transition: 'all 0.3s ease',
                                                opacity: isConversationResolved ? 0.6 : 1
                                            }}
                                        />
                                    )}
                                    {inputText.trim() && (
                                        <Button
                                            type="primary"
                                            shape="circle"
                                            icon={<ArrowUpOutlined style={{ color: dynamicIconColor }} />}
                                            onClick={handleSendMessage}
                                            disabled={isProcessing || isTyping || voiceStreaming || voiceStartRequested || isUserListening || isAssistantThinking || isConversationResolved}
                                            size="large"
                                            style={{
                                                backgroundColor: dynamicPrimaryColor,
                                                borderRadius: '12px',
                                                width: '48px',
                                                height: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                opacity: (isProcessing || isTyping) ? 0.6 : 1
                                            }}
                                        />
                                    )}

                                </div>
                            </Footer>
                        )}
                    </Layout>

                    {!isWeb && (
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<DownOutlined style={{ color: dynamicIconColor }} />}
                            size="large"
                            onClick={toggleMinimize}
                            style={{
                                background: dynamicPrimaryColor,
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'fixed',
                                bottom: 10,
                                right: 10,
                                zIndex: 1001
                            }}
                        />
                    )}
                </div>
            )}

            {/* File Upload Modal */}
            {/* <Modal
                title="Upload Document"
                open={fileModalVisible}
                onCancel={() => {
                    setFileModalVisible(false);
                    setSelectedFile(null);
                    setFileContent('');
                }}
                width="70%"
                style={{ maxWidth: 800 }}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button
                            onClick={() => {
                                setFileModalVisible(false);
                                setSelectedFile(null);
                                setFileContent('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleFileUpload}
                            disabled={!selectedFile}
                        >
                            Send Document
                        </Button>
                    </div>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Typography.Text type="secondary">
                        Upload a document (TXT, PDF, or other text files) to share with the assistant. The content will be analyzed and processed for better assistance.
                    </Typography.Text>
                </div>
                <Upload.Dragger
                    accept=".txt,.pdf,.doc,.docx"
                    showUploadList={false}
                    beforeUpload={handleFileSelect}
                    style={{
                        backgroundColor: selectedFile ? '#f6ffed' : undefined,
                        borderColor: selectedFile ? '#b7eb8f' : undefined,
                    }}
                >
                    {selectedFile ? (
                        <div>
                            <p className="ant-upload-drag-icon">
                                <FileTextOutlined style={{ color: '#52c41a' }} />
                            </p>
                            <p className="ant-upload-text" style={{ color: '#52c41a' }}>
                                ✓ {selectedFile.name} ({Math.round(selectedFile.size / 1024)}KB)
                            </p>
                            <p className="ant-upload-hint">File ready to send</p>
                        </div>
                    ) : (
                        <div>
                            <p className="ant-upload-drag-icon">
                                <UploadOutlined />
                            </p>
                            <p className="ant-upload-text">Click or drag file to this area to upload</p>
                            <p className="ant-upload-hint">Support for TXT, PDF, DOC, and other document formats</p>
                        </div>
                    )}
                </Upload.Dragger>
            </Modal> */}

        </div>
    );
}
